import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { produce } from "immer";
import { api } from "../api";
import type { PersonalEdition, EditionMeta, SummaryBox } from "../types";
import { Section } from "../components/Section";
import { Field, TextInput, TextArea, Toggle, BulletList } from "../components/Field";
import { RatesField } from "../components/RatesField";
import { ProjectionField } from "../components/ProjectionField";
import { CategoriesField } from "../components/CategoriesField";
import { VarsField } from "../components/VarsField";

type SaveState = "idle" | "saving" | "saved" | "error";

export default function Editor() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [meta, setMeta] = useState<EditionMeta | null>(null);
  const [data, setData] = useState<PersonalEdition | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirtyRef = useRef(false);

  // Initial load
  useEffect(() => {
    if (!id) return;
    api
      .getEdition(id)
      .then((r) => {
        setMeta(r.edition);
        setData(JSON.parse(r.edition.data_json) as PersonalEdition);
      })
      .catch((e) => setSaveError(e.message));
  }, [id]);

  const isPublished = meta?.status === "published";

  const save = useCallback(
    async (snapshot: PersonalEdition) => {
      if (!id || isPublished) return;
      setSaveState("saving");
      setSaveError(null);
      try {
        const r = await api.updateEdition(id, snapshot);
        setMeta(r.edition);
        setSaveState("saved");
        setPreviewKey((k) => k + 1);
      } catch (e: any) {
        setSaveError(e.message ?? "Save failed");
        setSaveState("error");
      }
    },
    [id, isPublished]
  );

  const update = useCallback(
    (recipe: (draft: PersonalEdition) => void) => {
      setData((curr) => {
        if (!curr) return curr;
        const next = produce(curr, recipe);
        dirtyRef.current = true;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
          dirtyRef.current = false;
          save(next);
        }, 800);
        return next;
      });
    },
    [save]
  );

  // Save on unmount if dirty
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  async function handlePublish() {
    if (!id || !meta) return;
    if (!confirm("Publish this edition? Once published it can't be edited (only duplicated).")) return;
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      if (data) await save(data);
    }
    try {
      const r = await api.publishEdition(id);
      setMeta(r.edition);
    } catch (e: any) {
      setSaveError(e.message);
    }
  }

  async function handleDuplicate() {
    if (!id) return;
    const r = await api.createEdition("personal", { cloneFromId: id });
    nav(`/editions/${r.edition.id}`);
  }

  function handleDownloadPdf() {
    if (!id) return;
    const a = document.createElement("a");
    a.href = `/api/editions/${id}/pdf`;
    a.click();
  }

  if (!data || !meta) {
    return <div className="p-10 text-neutral-500">Loading…</div>;
  }

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] h-screen">
      <div className="overflow-y-auto bg-neutral-50">
        <div className="px-8 py-6 border-b bg-white sticky top-0 z-10 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <button
              onClick={() => nav("/")}
              className="text-xs text-neutral-500 hover:text-neutral-900 mb-1"
            >
              ← Editions
            </button>
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-semibold truncate">
                {data.versionCode || "Untitled"}
              </h1>
              <StatusPill status={meta.status} />
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <SaveIndicator state={saveState} error={saveError} />
            <button
              onClick={handleDownloadPdf}
              className="rounded border border-neutral-300 px-4 py-2 text-sm font-medium hover:bg-neutral-100"
            >
              Download PDF
            </button>
            {!isPublished ? (
              <button
                onClick={handlePublish}
                className="rounded bg-neutral-900 text-white px-4 py-2 text-sm font-medium hover:bg-neutral-800"
              >
                Publish
              </button>
            ) : (
              <button
                onClick={handleDuplicate}
                className="rounded bg-neutral-900 text-white px-4 py-2 text-sm font-medium hover:bg-neutral-800"
              >
                Duplicate as draft
              </button>
            )}
          </div>
        </div>

        <div className="px-8 py-6 space-y-4">
          {isPublished && (
            <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              This edition is published and read-only. Click <em>Duplicate as draft</em> to make changes.
            </div>
          )}

          <Section title="Edition meta">
            <Field label="Version code" hint="e.g. 24153 Personal-Dep_PG_18_03_2026">
              <TextInput
                value={data.versionCode}
                onChange={(v) => update((d) => void (d.versionCode = v))}
              />
            </Field>
            <Field label="Effective date">
              <TextInput
                value={data.effectiveDate}
                onChange={(v) => update((d) => void (d.effectiveDate = v))}
              />
            </Field>
          </Section>

          <Section title="Cover">
            <Field label="Eyebrow">
              <TextInput
                value={data.cover.eyebrow}
                onChange={(v) => update((d) => void (d.cover.eyebrow = v))}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title (bold)">
                <TextInput
                  value={data.cover.title.bold}
                  onChange={(v) => update((d) => void (d.cover.title.bold = v))}
                />
              </Field>
              <Field label="Title (light)">
                <TextInput
                  value={data.cover.title.light}
                  onChange={(v) => update((d) => void (d.cover.title.light = v))}
                />
              </Field>
            </div>
            <Field label="Subtitle">
              <TextArea
                value={data.cover.subtitle}
                onChange={(v) => update((d) => void (d.cover.subtitle = v))}
                rows={2}
              />
            </Field>
          </Section>

          {data.boxes.map((box, idx) => (
            <BoxEditor
              key={box.productKey}
              box={box}
              onChange={(recipe) => update((d) => recipe(d.boxes[idx]))}
            />
          ))}

          <Section title="Back cover" defaultOpen={false}>
            <Field label="Hero title">
              <TextArea
                value={data.backCover.heroTitle}
                onChange={(v) => update((d) => void (d.backCover.heroTitle = v))}
                rows={2}
              />
            </Field>
            <Field label="Hero bullets">
              <BulletList
                value={data.backCover.heroBullets}
                onChange={(v) => update((d) => void (d.backCover.heroBullets = v))}
              />
            </Field>
            <Field label="Body copy">
              <TextArea
                value={data.backCover.body}
                onChange={(v) => update((d) => void (d.backCover.body = v))}
                rows={8}
              />
            </Field>
            <Field label="Band heading">
              <TextInput
                value={data.backCover.bandHeading}
                onChange={(v) => update((d) => void (d.backCover.bandHeading = v))}
              />
            </Field>
            <Field label="Categories">
              <CategoriesField
                value={data.backCover.categories}
                onChange={(v) => update((d) => void (d.backCover.categories = v))}
              />
            </Field>
            <Field label="Footer tagline">
              <TextInput
                value={data.backCover.footerTagline}
                onChange={(v) => update((d) => void (d.backCover.footerTagline = v))}
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Tel">
                <TextInput
                  value={data.backCover.contact.tel}
                  onChange={(v) => update((d) => void (d.backCover.contact.tel = v))}
                />
              </Field>
              <Field label="Email">
                <TextInput
                  value={data.backCover.contact.email}
                  onChange={(v) => update((d) => void (d.backCover.contact.email = v))}
                />
              </Field>
              <Field label="Web">
                <TextInput
                  value={data.backCover.contact.web}
                  onChange={(v) => update((d) => void (d.backCover.contact.web = v))}
                />
              </Field>
            </div>
            <Field label="Regulator line">
              <TextArea
                value={data.backCover.regulatorLine}
                onChange={(v) => update((d) => void (d.backCover.regulatorLine = v))}
                rows={3}
              />
            </Field>
          </Section>
        </div>
      </div>

      <div className="bg-neutral-200 overflow-hidden">
        <iframe
          key={previewKey}
          src={`/preview/editions/${id}?v=${previewKey}`}
          className="w-full h-full border-0 bg-white"
          title="Preview"
        />
      </div>
    </div>
  );
}

function BoxEditor({
  box,
  onChange,
}: {
  box: SummaryBox;
  onChange: (recipe: (draft: SummaryBox) => void) => void;
}) {
  return (
    <Section title={box.header} defaultOpen={false}>
      <Field label="Header">
        <TextInput
          value={box.header}
          onChange={(v) => onChange((b) => void (b.header = v))}
        />
      </Field>
      <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
        <Field label="Account name">
          <TextInput
            value={box.accountName}
            onChange={(v) => onChange((b) => void (b.accountName = v))}
          />
        </Field>
        <div className="pb-2">
          <Toggle
            value={box.onlineOnly}
            onChange={(v) => onChange((b) => void (b.onlineOnly = v))}
            label="Online Only"
          />
        </div>
      </div>

      <Field
        label="Variables"
        hint='Reference any of these in text fields as {key}. e.g. "BoE rate is currently {boeRate}".'
      >
        <VarsField
          value={box.vars ?? {}}
          onChange={(v) =>
            onChange((b) => {
              if (Object.keys(v).length === 0) delete b.vars;
              else b.vars = v;
            })
          }
        />
      </Field>

      {box.rateBlock.kind === "tracker" ? (
        <>
          <Field label="Tracker — intro">
            <TextArea
              value={box.rateBlock.intro}
              onChange={(v) =>
                onChange((b) => {
                  if (b.rateBlock.kind === "tracker") b.rateBlock.intro = v;
                })
              }
              rows={5}
            />
          </Field>
          {box.rateBlock.tables.map((t, ti) => (
            <div
              key={ti}
              className="rounded border border-neutral-200 p-3 space-y-2"
            >
              <div className="grid grid-cols-2 gap-2">
                <Field label="Section heading">
                  <TextInput
                    value={t.heading}
                    onChange={(v) =>
                      onChange((b) => {
                        if (b.rateBlock.kind === "tracker")
                          b.rateBlock.tables[ti].heading = v;
                      })
                    }
                  />
                </Field>
                <Field label="Margin column label">
                  <TextInput
                    value={t.marginLabel}
                    onChange={(v) =>
                      onChange((b) => {
                        if (b.rateBlock.kind === "tracker")
                          b.rateBlock.tables[ti].marginLabel = v;
                      })
                    }
                  />
                </Field>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <Field label="Intro bonus">
                  <TextInput
                    value={t.row.introBonus}
                    onChange={(v) =>
                      onChange((b) => {
                        if (b.rateBlock.kind === "tracker")
                          b.rateBlock.tables[ti].row.introBonus = v;
                      })
                    }
                  />
                </Field>
                <Field label="Margin">
                  <TextInput
                    value={t.row.margin}
                    onChange={(v) =>
                      onChange((b) => {
                        if (b.rateBlock.kind === "tracker")
                          b.rateBlock.tables[ti].row.margin = v;
                      })
                    }
                  />
                </Field>
                <Field label="Gross rate">
                  <TextInput
                    value={t.row.grossRate}
                    onChange={(v) =>
                      onChange((b) => {
                        if (b.rateBlock.kind === "tracker")
                          b.rateBlock.tables[ti].row.grossRate = v;
                      })
                    }
                  />
                </Field>
                <Field label="AER">
                  <TextInput
                    value={t.row.aer}
                    onChange={(v) =>
                      onChange((b) => {
                        if (b.rateBlock.kind === "tracker")
                          b.rateBlock.tables[ti].row.aer = v;
                      })
                    }
                  />
                </Field>
              </div>
            </div>
          ))}
          <Field label="Tracker — outro">
            <TextArea
              value={box.rateBlock.outro}
              onChange={(v) =>
                onChange((b) => {
                  if (b.rateBlock.kind === "tracker") b.rateBlock.outro = v;
                })
              }
              rows={4}
            />
          </Field>
        </>
      ) : (
        <>
          <Field label="Rates">
            <RatesField
              value={box.rateBlock.rates}
              onChange={(v) =>
                onChange((b) => {
                  if (b.rateBlock.kind !== "tracker") b.rateBlock.rates = v;
                })
              }
            />
          </Field>
          <Field label="Rate explainer">
            <TextArea
              value={box.rateBlock.body}
              onChange={(v) =>
                onChange((b) => {
                  if (b.rateBlock.kind !== "tracker") b.rateBlock.body = v;
                })
              }
              rows={4}
            />
          </Field>
        </>
      )}

      <div className="grid grid-cols-[auto_1fr] gap-3 items-start">
        <Toggle
          value={box.rateChangeable.canChange}
          onChange={(v) => onChange((b) => void (b.rateChangeable.canChange = v))}
          label="Bank can change the rate"
        />
        <Field label="Rate-change explainer">
          <TextArea
            value={box.rateChangeable.body}
            onChange={(v) => onChange((b) => void (b.rateChangeable.body = v))}
            rows={3}
          />
        </Field>
      </div>

      <Field
        label="Projection question"
        hint="Optional — overrides the accordion question for this product"
      >
        <TextInput
          value={box.projection.question ?? ""}
          onChange={(v) =>
            onChange((b) => {
              if (v.trim()) b.projection.question = v;
              else delete (b.projection as { question?: string }).question;
            })
          }
        />
      </Field>
      <Field label="Projection heading">
        <TextInput
          value={box.projection.heading}
          onChange={(v) => onChange((b) => void (b.projection.heading = v))}
        />
      </Field>
      <Field label="Projection explainer">
        <TextArea
          value={box.projection.body}
          onChange={(v) => onChange((b) => void (b.projection.body = v))}
          rows={3}
        />
      </Field>
      <Field label="Projection items">
        <ProjectionField
          value={box.projection.items}
          onChange={(v) => onChange((b) => void (b.projection.items = v))}
        />
      </Field>

      <Field label="Opening — intro">
        <TextInput
          value={box.opening.intro}
          onChange={(v) => onChange((b) => void (b.opening.intro = v))}
        />
      </Field>
      <Field label="Opening — bullets">
        <BulletList
          value={box.opening.bullets}
          onChange={(v) => onChange((b) => void (b.opening.bullets = v))}
          rows={6}
        />
      </Field>
      <Field label="Managing — intro">
        <TextInput
          value={box.managing.intro}
          onChange={(v) => onChange((b) => void (b.managing.intro = v))}
        />
      </Field>
      <Field label="Managing — bullets">
        <BulletList
          value={box.managing.bullets}
          onChange={(v) => onChange((b) => void (b.managing.bullets = v))}
          rows={3}
        />
      </Field>

      <Field label="Withdrawal — summary">
        <TextArea
          value={box.withdrawal.summary}
          onChange={(v) => onChange((b) => void (b.withdrawal.summary = v))}
          rows={2}
        />
      </Field>
      <Field label="Withdrawal — paragraphs" hint="One paragraph per line">
        <BulletList
          value={box.withdrawal.paragraphs}
          onChange={(v) => onChange((b) => void (b.withdrawal.paragraphs = v))}
          rows={6}
        />
      </Field>

      <Field label="Additional info" hint="One line per paragraph">
        <BulletList
          value={box.additionalInfo}
          onChange={(v) => onChange((b) => void (b.additionalInfo = v))}
          rows={4}
        />
      </Field>
    </Section>
  );
}

function StatusPill({ status }: { status: "draft" | "published" }) {
  const styles =
    status === "published"
      ? "bg-emerald-100 text-emerald-800"
      : "bg-amber-100 text-amber-800";
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider rounded px-2 py-0.5 ${styles}`}>
      {status}
    </span>
  );
}

function SaveIndicator({
  state,
  error,
}: {
  state: SaveState;
  error: string | null;
}) {
  if (state === "saving") return <span className="text-xs text-neutral-500">Saving…</span>;
  if (state === "saved") return <span className="text-xs text-emerald-600">Saved</span>;
  if (state === "error")
    return <span className="text-xs text-red-600" title={error ?? ""}>Save failed</span>;
  return null;
}
